import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Skeleton, Spin, Tag, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, LinkOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_DOMAIN } from "../constants";

const authHeaders = () => ({ accessToken: localStorage.getItem("accessToken") });

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

function StatusRow({ label, refreshing, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
      <span>{label}</span>
      {refreshing ? <Skeleton.Button active size="small" shape="round" style={{ width: 96 }} /> : children}
    </div>
  );
}

// Mirrors the backend's /zoho-books flow (see routes/zoho_books.js's own
// runbook comment): GET /status on open, GET /auth-url on click (opened in
// a new tab so the consent screen and this modal can stay side by side),
// then the user comes back and clicks Refresh Status once Zoho redirects
// their new tab through the OAuth callback — there's no way for this tab
// to know that finished on its own.
export function ZohoConnectionPanel() {
  const [status, setStatus] = useState(null); // { configured, connected, has_organization, config }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Armed right after a consent tab is opened, so the next time this tab
  // regains focus (the user tabbing back after accepting/declining in the
  // Zoho tab) we can refresh status on their behalf — there's no message
  // back from that tab, so visibility regain is the closest signal we get.
  // Disarmed after firing once, so idle tab-switching afterwards doesn't
  // keep re-triggering it.
  const awaitingConsentRef = useRef(false);

  const loadStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setLoadError("");
    try {
      const res = await axios.get(`${API_DOMAIN}/zoho-books/status`, { headers: authHeaders() });
      if (res.data?.success) setStatus(res.data.data);
      else setLoadError(res.data?.message || "Failed to load Zoho connection status.");
    } catch (error) {
      setLoadError(getErrorMessage(error, "Failed to load Zoho connection status."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    loadStatus(true);
  }, [loadStatus]);

  // Auto-refresh once this tab becomes visible again, but only if it was
  // armed by a Connect/Reconnect click — otherwise every unrelated
  // alt-tab back to the browser would fire a status check.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && awaitingConsentRef.current) {
        awaitingConsentRef.current = false;
        handleRefresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [handleRefresh]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API_DOMAIN}/zoho-books/auth-url`, { headers: authHeaders() });
      if (res.data?.success && res.data.data?.auth_url) {
        // noopener/noreferrer means window.open() always returns null (by
        // design — this tab gets no reference to the new one), so it can't
        // tell us whether a popup blocker silently ate the tab. Arm
        // unconditionally; the worst case if it was blocked is one harmless
        // extra status check next time this tab regains focus.
        window.open(res.data.data.auth_url, "_blank", "noopener,noreferrer");
        awaitingConsentRef.current = true;
      } else {
        setLoadError(res.data?.message || "Failed to generate the Zoho consent URL.");
      }
    } catch (error) {
      setLoadError(getErrorMessage(error, "Failed to generate the Zoho consent URL."));
    } finally {
      setConnecting(false);
    }
  };

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><Spin /></div>;

  const connected = !!status?.connected;
  const configured = !!status?.configured;
  const hasOrganization = !!status?.has_organization;
  const cfg = status?.config || {};
  const lastError = cfg.last_error
    ? (typeof cfg.last_error === "string" ? cfg.last_error : cfg.last_error.message || JSON.stringify(cfg.last_error))
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {loadError && <Alert type="error" showIcon message={loadError} />}

      <div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Connection Status</div>
        <div className="rounded-xl p-3" style={{ border: "1px solid #f0f0f0" }}>
          <StatusRow label="Overall" refreshing={refreshing}>
            <Tag color={connected ? "success" : "default"} icon={connected ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
              {connected ? "Connected" : "Not connected"}
            </Tag>
          </StatusRow>
          <StatusRow label="Client configured" refreshing={refreshing}>
            <Tag color={configured ? "success" : "error"}>{configured ? "Yes" : "No"}</Tag>
          </StatusRow>
          <StatusRow label="Organization selected" refreshing={refreshing}>
            <Tag color={hasOrganization ? "success" : "default"}>
              {hasOrganization ? (cfg.organization_name || cfg.organization_id) : "None"}
            </Tag>
          </StatusRow>
          {!refreshing && cfg.connected_at && (
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>Connected at: {cfg.connected_at}</div>
          )}
          {!refreshing && lastError && (
            <Alert type="warning" showIcon style={{ marginTop: 10 }} message="Last error" description={lastError} />
          )}
        </div>
      </div>

      <div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Connect/Reconnect to Zoho</div>
        <div className="rounded-xl p-3" style={{ border: "1px solid #f0f0f0", display: "flex", gap: 10 }}>
          <Button type="primary" icon={<LinkOutlined />} loading={connecting} onClick={handleConnect}>
            {connected ? "Reconnect to Zoho" : "Connect to Zoho"}
          </Button>
          <Button icon={<ReloadOutlined />} loading={refreshing} onClick={handleRefresh}>
            Refresh Status
          </Button>
        </div>
        <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 8 }}>
          Connect opens Zoho's consent screen in a new tab. After accepting, come back here and click Refresh Status to confirm.
        </Typography.Text>
      </div>
    </div>
  );
}
